import { useEffect, useState, type ReactNode } from "react";
import { Avatar, Banner, Button, Card, Spinner, Switch, TextField, ThemeChoice, useToast } from "@vavr/ui";
import { formatUsPhone, toUsE164 } from "@vavr/core";
import { api, ApiError } from "../shared/api.ts";
import { useAsync } from "../shared/useAsync.ts";
import { addPasskey, passkeysSupported } from "../shared/webauthn.ts";
import type { PushBridge } from "../shared/platform.ts";
import { useGuardian } from "./context.tsx";
import { DeleteAccountCard } from "../auth/DeleteAccount.tsx";
import { SupportCard } from "../support/HelpDesk.tsx";
import { LegalLinks } from "./SignIn.tsx";

/** Registers this phone's FCM token for the given app. Returns false if the person declined. */
export async function registerPush(push: PushBridge, app: "guardian" | "teacher"): Promise<boolean> {
  const r = await push.register(app);
  if (!r) return false;
  await api("POST", "/api/v1/push-tokens", { token: r.token, platform: r.platform, app });
  return true;
}

export function NoFaceRecognition() {
  return (
    <Card title="Your privacy" description="Vavr takes a photo of the adult at the door so staff can compare it by eye.">
      <ul className="pmu-g-bullets">
        <li>No facial recognition, ever. No face templates are made or stored.</li>
        <li>You see the photo and signature only for adults you added yourself.</li>
        <li>Children never have accounts.</li>
      </ul>
    </Card>
  );
}

function Notifications() {
  const { platform } = useGuardian();
  const [perm, setPerm] = useState<"granted" | "denied" | "prompt" | null>(null);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  useEffect(() => { platform.push?.permission().then(setPerm, () => setPerm(null)); }, [platform]);
  if (!platform.push) {
    return <Card title="Notifications" description="On the website we email you when your child is picked up or dropped off. Install the app to get instant notifications." />;
  }
  return (
    <Card title="Notifications" description="A notification the moment your child is picked up or dropped off. If you turn them off, we email you instead.">
      {perm === "granted" ? <p>Turned on for this phone.</p> : perm === "denied" ? (
        <Banner tone="info" title="Notifications are off for Vavr">Turn them on in your phone's Settings, under Notifications. We email you in the meantime.</Banner>
      ) : (
        <Button loading={busy} onClick={async () => {
          setBusy(true);
          try { const ok = await registerPush(platform.push!, "guardian"); setPerm(ok ? "granted" : "denied"); if (ok) toast.show({ title: "Notifications are on", tone: "success" }); }
          catch (e) { toast.show({ title: (e as Error).message, tone: "danger" }); }
          finally { setBusy(false); }
        }}>Turn on notifications</Button>
      )}
    </Card>
  );
}

interface SmsState {
  enabled: boolean;
  phone: string | null;
  consent: { granted: boolean; source: string; ts: number } | null;
  text: { id: string; title: string; text: string };
}

/** Exception texts (PMU-163): the parent's number and their consent, against the wording shown. */
function TextMessages() {
  const sms = useAsync(() => api<SmsState>("GET", "/api/v1/me/sms"), []);
  const [typed, setTyped] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try { await fn(); sms.reload(); }
    catch (e) { toast.show({ title: e instanceof ApiError ? e.message : "That did not save. Try again.", tone: "danger" }); }
    finally { setBusy(false); }
  };
  const card = (body: ReactNode) => (
    <Card title="Text messages" description="Most news reaches you in the app or by email. Texts are only for the few things that need you at once.">{body}</Card>
  );
  // Nothing shows until the server says texts are offered (PMU_SMS): no card flashes when they are off.
  if (!sms.data?.enabled) return null;
  const { phone, consent, text } = sms.data;
  const editing = typed !== null || !phone;
  const savePhone = () => {
    const e164 = toUsE164(typed ?? "");
    if (!e164) { setError("Enter a US mobile number, like (512) 555-0188."); return; }
    setError(null);
    void run(async () => { await api("PATCH", "/api/v1/me", { phone: e164 }); setTyped(null); });
  };
  return card(
    <div className="pmu-stack">
      {editing ? (
        <form className="pmu-stack" onSubmit={(e) => { e.preventDefault(); savePhone(); }}>
          <TextField label="Mobile number" type="tel" inputMode="tel" autoComplete="tel" hint="US numbers only." error={error ?? undefined}
            value={typed ?? ""} onChange={(e) => setTyped(e.target.value)} />
          <div className="pmu-row">
            <Button type="submit" size="sm" loading={busy}>Save number</Button>
            {phone && <Button size="sm" variant="ghost" onClick={() => { setTyped(null); setError(null); }}>Cancel</Button>}
          </div>
        </form>
      ) : (
        <div className="pmu-row pmu-row--between">
          <span>{formatUsPhone(phone!)}</span>
          <Button size="sm" variant="ghost" onClick={() => setTyped("")}>Change</Button>
        </div>
      )}
      {phone && !editing && (
        <Switch label={text.title} description={text.text} checked={consent?.granted === true} disabled={busy}
          onCheckedChange={(on) => run(() => api("PUT", "/api/v1/me/sms", { granted: on, text_id: text.id }))} />
      )}
      {phone && !editing && consent && !consent.granted && consent.source.startsWith("sms:") && (
        <p className="pmu-small pmu-muted">Texts are off because you replied STOP to one of them. Turn them on here, or reply START.</p>
      )}
    </div>,
  );
}

function Passkeys() {
  const { platform } = useGuardian();
  const me = useAsync(() => api("GET", "/api/v1/me"), []);
  const [msg, setMsg] = useState<{ tone: "success" | "danger"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  if (!platform.passkeys || !passkeysSupported()) {
    return <Card title="Signing in" description="You sign in with a link we email you. You stay signed in on this phone for 90 days." />;
  }
  const run = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true); setMsg(null);
    try { await fn(); setMsg({ tone: "success", text: ok }); me.reload(); }
    catch (e) { setMsg({ tone: "danger", text: e instanceof ApiError ? e.message : "The passkey was not saved. Try again." }); }
    finally { setBusy(false); }
  };
  return (
    <Card title="Passkeys" description="Sign in with your fingerprint, face or phone PIN instead of an email link.">
      <div className="pmu-stack">
        {msg && <Banner tone={msg.tone} title={msg.text} />}
        {me.loading ? <Spinner label="Loading passkeys" /> : me.data?.passkeys.map((p: { credential_id: string; name: string }) => (
          <div key={p.credential_id} className="pmu-row pmu-row--between">
            <span>{p.name}</span>
            <Button size="sm" variant="ghost" onClick={() => run(() => api("DELETE", `/api/v1/auth/passkeys/${encodeURIComponent(p.credential_id)}`), "Passkey removed.")}>Remove</Button>
          </div>
        ))}
        <Button icon="lock" variant="secondary" loading={busy} onClick={() => run(() => addPasskey("Phone"), "Passkey added.")}>Add a passkey</Button>
      </div>
    </Card>
  );
}

/** An optional password (PMU-241): sign in without opening your email. */
function PasswordCard({ has, onSaved }: { has: boolean; onSaved: () => void }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ tone: "success" | "danger"; text: string } | null>(null);
  const save = async () => {
    setBusy(true); setMsg(null);
    try { await api("PUT", "/api/v1/me/password", { current: has ? current : undefined, password: next }); setMsg({ tone: "success", text: "Password saved." }); setOpen(false); setCurrent(""); setNext(""); onSaved(); }
    catch (e) { setMsg({ tone: "danger", text: e instanceof ApiError ? e.message : "The password was not saved. Try again." }); }
    finally { setBusy(false); }
  };
  return (
    <Card title="Password" description={has ? "You can sign in with your email and password, or with an email link." : "Optional. Add a password to sign in without opening your email."}>
      <div className="pmu-stack">
        {msg && <Banner tone={msg.tone} title={msg.text} />}
        {!open ? <Button variant="secondary" icon="lock" onClick={() => setOpen(true)}>{has ? "Change your password" : "Add a password"}</Button> : (
          <>
            {has && <TextField label="Current password" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />}
            <TextField label="New password" type="password" autoComplete="new-password" hint="At least 12 characters. A few words work well." value={next} onChange={(e) => setNext(e.target.value)} />
            <div className="pmu-row"><Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button><Button loading={busy} disabled={next.length < 12} onClick={() => void save()}>Save</Button></div>
          </>
        )}
      </div>
    </Card>
  );
}

export function Settings() {
  const { signOut } = useGuardian();
  const me = useAsync(() => api("GET", "/api/v1/me"), []);
  return (
    <div className="pmu-stack pmu-g-settings">
      {me.data && (
        <div className="pmu-g-profile pmu-g-profile--center">
          <Avatar kind="adult" size="xl" name={me.data.account.name} />
          <div><h2 className="pmu-g-profile__name">{me.data.account.name}</h2><p className="pmu-muted">{me.data.account.email}</p></div>
        </div>
      )}
      <Card title="Appearance" description="Light, dark, or follow your phone's setting."><ThemeChoice /></Card>
      <Notifications />
      <TextMessages />
      <Passkeys />
      {me.data && <PasswordCard has={!!me.data.account.has_password} onSaved={me.reload} />}
      <NoFaceRecognition />
      <Button variant="secondary" icon="logout" onClick={() => signOut()} fullWidth>Sign out</Button>
      <SupportCard />
      {me.data && <DeleteAccountCard email={me.data.account.email} onDeleted={() => void signOut()} />}
      <p className="pmu-small pmu-muted pmu-g-center">For adults only. Vavr does not have accounts for children.</p>
      <LegalLinks />
    </div>
  );
}
